// =============================================================================
// Service Step — selezione servizio
// =============================================================================

import { useTranslation } from 'react-i18next';
import { Clock, ChevronRight, Loader2 } from 'lucide-react';
import type { PublicService } from '../../api/public-booking-hooks';

const PRIMARY = 'var(--skedulab-public-primary, #10B981)';

interface Props {
  services: PublicService[];
  isLoading: boolean;
  onSelect: (svc: PublicService) => void;
}

export function ServiceStep({ services, isLoading, onSelect }: Props) {
  const { t } = useTranslation('public-booking');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('loading')}
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 text-sm">{t('service.noServices')}</div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">{t('service.title')}</h2>
      <ul className="space-y-2.5">
        {services.map((svc) => (
          <li key={svc.id}>
            <button
              onClick={() => onSelect(svc)}
              className="group w-full flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white text-left transition-all hover:shadow-sm hover:-translate-y-px"
              style={{ ['--tw-ring-color' as string]: PRIMARY }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = PRIMARY)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '')}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {svc.color && (
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: svc.color }}
                    />
                  )}
                  <span className="text-sm font-semibold text-gray-900 truncate">{svc.name}</span>
                </div>
                {svc.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{svc.description}</p>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t('service.duration', { minutes: svc.durationMinutes })}
                  </span>
                  {svc.basePrice !== null && svc.basePrice > 0 && (
                    <span className="font-medium">
                      {t('service.from')} € {svc.basePrice.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight
                className="w-5 h-5 text-gray-300 flex-shrink-0 transition-colors group-hover:text-[color:var(--skedulab-public-primary)]"
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
